import { FormEvent, useState } from 'react'
import { submitContact } from '../utils/contactSubmit'
import type { ContactPayload } from '../utils/contactSubmit'
import { validateContact } from '../utils/contactValidation'
import type { ContactErrors, ContactFields } from '../utils/contactValidation'

export type ContactStatus = 'idle' | 'submitting' | 'success' | 'error'

const EMPTY_FIELDS: ContactFields = {
  name: '',
  email: '',
  phone: '',
  message: '',
  consent: false,
}

const FIELD_ORDER: (keyof ContactFields)[] = ['name', 'email', 'phone', 'message', 'consent']

interface UseContactFormOptions {
  idPrefix: string
  source: ContactPayload['source']
  requireMessage: boolean
}

export function useContactForm({ idPrefix, source, requireMessage }: UseContactFormOptions) {
  const [fields, setFields] = useState<ContactFields>(EMPTY_FIELDS)
  const [errors, setErrors] = useState<ContactErrors>({})
  const [status, setStatus] = useState<ContactStatus>('idle')

  const updateField = <K extends keyof ContactFields>(key: K, value: ContactFields[K]) => {
    setFields((prev) => ({ ...prev, [key]: value }))
    if (errors[key]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[key]
        return next
      })
    }
    if (status === 'error') setStatus('idle')
  }

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (status === 'submitting') return

    const nextErrors = validateContact(fields, requireMessage)
    setErrors(nextErrors)
    const firstInvalid = FIELD_ORDER.find((key) => nextErrors[key])
    if (firstInvalid) {
      document.getElementById(`${idPrefix}-${firstInvalid}`)?.focus()
      return
    }

    setStatus('submitting')
    try {
      await submitContact({
        name: fields.name.trim(),
        email: fields.email.trim(),
        phone: fields.phone.trim(),
        message: fields.message.trim(),
        source,
      })
      setStatus('success')
      setFields(EMPTY_FIELDS)
    } catch {
      setStatus('error')
    }
  }

  const reset = () => {
    setStatus('idle')
    setErrors({})
  }

  return { fields, errors, status, updateField, handleSubmit, reset }
}
